/**
 * Clipboard read/write with browser fallbacks for the Slate email editor.
 * Multi-type ClipboardItem often fails (Safari, Firefox, strict policies); paste via read() needs permission.
 */

function fallbackExecCommandCopy(text: string): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Write HTML + plain text to the system clipboard (best effort). */
export async function writeHtmlAndPlainToClipboard(html: string, plain: string): Promise<void> {
  const hasWrite =
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.write === 'function';
  const secure = typeof window !== 'undefined' && window.isSecureContext;

  if (!hasWrite || !secure) {
    if (fallbackExecCommandCopy(plain)) return;
    throw new Error('Clipboard is not available. Use HTTPS or keyboard shortcuts (Ctrl+C / Ctrl+X).');
  }

  const attempts: Array<() => Promise<void>> = [];

  if (typeof ClipboardItem !== 'undefined') {
    attempts.push(async () => {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
    });
    // Safari expects Promise<Blob> values in some versions
    attempts.push(async () => {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': Promise.resolve(new Blob([html], { type: 'text/html' })),
          'text/plain': Promise.resolve(new Blob([plain], { type: 'text/plain' })),
        }),
      ]);
    });
    attempts.push(async () => {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
    });
    attempts.push(async () => {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': Promise.resolve(new Blob([plain], { type: 'text/plain' })),
        }),
      ]);
    });
  }

  if (typeof navigator.clipboard.writeText === 'function') {
    attempts.push(() => navigator.clipboard.writeText(plain));
  }

  let lastError: unknown;
  for (const run of attempts) {
    try {
      await run();
      return;
    } catch (e) {
      lastError = e;
    }
  }
  if (fallbackExecCommandCopy(plain)) return;
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

export type ClipboardReadResult =
  | { status: 'ok'; html?: string; plain?: string }
  | { status: 'empty' }
  | { status: 'denied' };

/** Plain text from HTML (for paste-without-formatting when only HTML is on the clipboard). */
export function htmlToPlainText(html: string): string {
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim();
}

async function readClipboardViaReadApi(): Promise<ClipboardReadResult> {
  if (typeof navigator.clipboard.read !== 'function') {
    return { status: 'denied' };
  }
  try {
    const items = await navigator.clipboard.read();
    if (!items.length) return { status: 'empty' };
    const item = items[0];
    let html: string | undefined;
    let plain: string | undefined;
    if (item.types.includes('text/html')) {
      html = await (await item.getType('text/html')).text();
    }
    if (item.types.includes('text/plain')) {
      plain = await (await item.getType('text/plain')).text();
    }
    if (html?.trim() || plain?.length) {
      return { status: 'ok', html: html?.trim() ? html : undefined, plain };
    }
    return { status: 'empty' };
  } catch {
    return { status: 'denied' };
  }
}

/** Read clipboard for rich paste (HTML and/or plain). Tries read() first, then readText(). */
export async function readClipboardForPaste(): Promise<ClipboardReadResult> {
  const hasApi = typeof navigator !== 'undefined' && navigator.clipboard;
  const secure = typeof window !== 'undefined' && window.isSecureContext;

  if (!hasApi || !secure) {
    return { status: 'denied' };
  }

  const fromRead = await readClipboardViaReadApi();
  if (fromRead.status === 'ok') return fromRead;
  if (fromRead.status === 'empty') return fromRead;

  if (typeof navigator.clipboard.readText === 'function') {
    try {
      const plain = await navigator.clipboard.readText();
      if (plain.length) return { status: 'ok', plain };
      return { status: 'empty' };
    } catch {
      return { status: 'denied' };
    }
  }

  return { status: 'denied' };
}

/**
 * Read clipboard as plain text for “Paste without formatting”.
 * Uses readText() first — many browsers allow it when read() is blocked or prompts fail.
 * If text is empty, falls back to read() and strips HTML to plain.
 */
export async function readClipboardForPlainPaste(): Promise<ClipboardReadResult> {
  const hasApi = typeof navigator !== 'undefined' && navigator.clipboard;
  const secure = typeof window !== 'undefined' && window.isSecureContext;

  if (!hasApi || !secure) {
    return { status: 'denied' };
  }

  if (typeof navigator.clipboard.readText === 'function') {
    try {
      const plain = await navigator.clipboard.readText();
      if (plain.length) {
        return { status: 'ok', plain };
      }
    } catch {
      // fall through to read()
    }
  }

  const fromRead = await readClipboardViaReadApi();
  if (fromRead.status !== 'ok') {
    return fromRead;
  }
  const { html, plain } = fromRead;
  if (plain?.length) {
    return { status: 'ok', plain };
  }
  if (html?.trim()) {
    const stripped = htmlToPlainText(html);
    if (stripped.length) return { status: 'ok', plain: stripped };
  }
  return { status: 'empty' };
}
